import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { logger } from '../utils/logger';

// Import existing market configuration
export const SHOPIFY_MARKETS = {
  de: {
    name: 'Deutschland',
    flag: '🇩🇪',
    currency: 'EUR',
    currencySymbol: '€',
    countryCode: 'DE',
    shopifyCountry: 'DE',
  },
  pl: {
    name: 'Polska',
    flag: '🇵🇱',
    currency: 'PLN',
    currencySymbol: 'zł',
    countryCode: 'PL',
    shopifyCountry: 'PL',
  },
} as const;

export type MarketCode = keyof typeof SHOPIFY_MARKETS;

// Browser location to market mapping
const BROWSER_COUNTRY_MAP: Record<string, MarketCode> = {
  DE: 'de',
  AT: 'de',
  PL: 'pl',
  CZ: 'de',
  SK: 'de',
  HU: 'de',
  CH: 'de',
  LI: 'de',
  LT: 'pl',
  LV: 'pl',
  EE: 'pl',
};

function detectBrowserMarket(): MarketCode {
  const browserLanguages = navigator.languages || [navigator.language];

  for (const lang of browserLanguages) {
    const countryCode = lang.split('-')[1]?.toUpperCase();
    if (countryCode && BROWSER_COUNTRY_MAP[countryCode]) {
      return BROWSER_COUNTRY_MAP[countryCode];
    }
  }

  return 'de';
}

interface MarketStore {
  // State
  currentMarket: MarketCode;
  availableMarkets: typeof SHOPIFY_MARKETS;
  productPrices: Record<string, number>;
  lastMarketChange: number;

  // Actions
  changeMarket: (newMarket: MarketCode) => void;
  updateProductPrice: (productId: string, price: number) => void;
  bulkUpdatePrices: (prices: Record<string, number>) => void;

  // Getters
  getCurrentMarketInfo: () => (typeof SHOPIFY_MARKETS)[MarketCode];
  getMarketCurrency: (market?: MarketCode) => string;
  getMarketCountryCode: (market?: MarketCode) => string;
  formatPrice: (amount: number, market?: MarketCode) => string;
  getProductPrice: (productId: string, fallbackPrice?: number) => number;
  isMarketActive: (market: string) => market is MarketCode;

  // Utilities
  loadFromStorage: () => void;
  resetStore: () => void;
  debugInfo: () => object;
}

export const useMarketStore = create<MarketStore>()(
  persist(
    subscribeWithSelector((set, get) => ({
      // Initial State
      currentMarket: 'de',
      availableMarkets: SHOPIFY_MARKETS,
      productPrices: {},
      lastMarketChange: 0,

      // Actions
      changeMarket: (newMarket: MarketCode) => {
        const currentMarket = get().currentMarket;
        if (currentMarket === newMarket) return;

        logger.log(
          '🌍 MarketStore: Changing market from',
          currentMarket,
          'to',
          newMarket
        );

        set({
          currentMarket: newMarket,
          productPrices: {}, // Clear prices for new market
          lastMarketChange: Date.now(),
        });

        // Analytics tracking
        if (typeof window !== 'undefined' && (window as any).gtag) {
          (window as any).gtag('event', 'market_change', {
            old_market: currentMarket,
            new_market: newMarket,
          });
        }

        logger.log('📍 MarketStore: Market changed to', newMarket);
      },

      updateProductPrice: (productId: string, price: number) => {
        const currentState = get();
        set({
          productPrices: {
            ...currentState.productPrices,
            [productId]: price,
          },
        });
      },

      bulkUpdatePrices: (prices: Record<string, number>) => {
        const currentState = get();
        set({
          productPrices: {
            ...currentState.productPrices,
            ...prices,
          },
        });
      },

      // Getters
      getCurrentMarketInfo: () => {
        return SHOPIFY_MARKETS[get().currentMarket];
      },

      getMarketCurrency: (market?: MarketCode) => {
        return SHOPIFY_MARKETS[market || get().currentMarket].currency;
      },

      getMarketCountryCode: (market?: MarketCode) => {
        return SHOPIFY_MARKETS[market || get().currentMarket].shopifyCountry;
      },

      formatPrice: (amount: number, market?: MarketCode) => {
        const marketInfo = SHOPIFY_MARKETS[market || get().currentMarket];
        const formattedAmount = amount.toFixed(2);

        // Format: amount + space + symbol (for EUR and PLN)
        return `${formattedAmount} ${marketInfo.currencySymbol}`;
      },

      getProductPrice: (productId: string, fallbackPrice?: number) => {
        const contextPrice = get().productPrices[productId];
        const finalPrice = contextPrice ?? fallbackPrice ?? 0;
        logger.log(
          '💰 MarketStore: getProductPrice for',
          productId,
          '- context:',
          contextPrice,
          'fallback:',
          fallbackPrice,
          'final:',
          finalPrice
        );
        return finalPrice;
      },

      isMarketActive: (market: string): market is MarketCode => {
        return market in SHOPIFY_MARKETS;
      },

      // Utilities
      loadFromStorage: () => {
        // This function is no longer needed as persist middleware handles it
        logger.log(
          'Store loadFromStorage called - using persist middleware instead'
        );
      },

      resetStore: () => {
        set({
          currentMarket: 'de',
          availableMarkets: SHOPIFY_MARKETS,
          productPrices: {},
          lastMarketChange: 0,
        });
      },

      debugInfo: () => ({
        currentMarket: get().currentMarket,
        productPricesCount: Object.keys(get().productPrices).length,
        lastMarketChange: new Date(get().lastMarketChange).toISOString(),
      }),
    })),
    {
      name: 'olispices-market-store',
      partialize: state => ({
        currentMarket: state.currentMarket,
        productPrices: state.productPrices,
      }),
      onRehydrateStorage: () => state => {
        if (state && !SHOPIFY_MARKETS[state.currentMarket]) {
          // If stored market is invalid, fallback to German market
          state.currentMarket = 'de';
        }
      },
    }
  )
);

// Store is automatically initialized by persist middleware

// Hook for components that need to track market changes
export const useMarketChangeDetection = () => {
  const currentMarket = useMarketStore(state => state.currentMarket);

  return { currentMarket };
};

// Selector hooks for performance optimization
export const useCurrentMarket = () =>
  useMarketStore(state => state.currentMarket);
export const useAvailableMarkets = () =>
  useMarketStore(state => state.availableMarkets);
export const useProductPrices = () =>
  useMarketStore(state => state.productPrices);
export const useMarketActions = () =>
  useMarketStore(state => ({
    changeMarket: state.changeMarket,
    updateProductPrice: state.updateProductPrice,
    bulkUpdatePrices: state.bulkUpdatePrices,
  }));
